import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { BaseResponse, HttpMethod, RequestOptions } from '@/types/api';
import { getToken } from './token';
import { getClientHeaders } from './clientHeaders';

// 创建默认的axios实例
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_APP_API_BASE,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': import.meta.env.VITE_APP_ACCEPT_LANGUAGE
    },
  });

  // 请求拦截器
  instance.interceptors.request.use(
    async (config) => {
      // 在这里可以添加token等认证信息
      const token = await getToken();
      // console.log('请求信息', config, token);
      // 不需要token的地址
      const BLACKLIST_URLS = [
        'https://files.tcgcard.jp/configs/config/jp/jp/secondary_config_test.json',
        'https://files.tcgasset.com/configs/config/jp/jp/secondary_config.json',
        '/api/market/evonet/key'
      ];
      // 判断当前请求是否为黑名单
      if (config.url && BLACKLIST_URLS.includes(config.url)) {
        // 不设置 Authorization
      } else if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // 添加客户端请求头到全局请求头
      try {
        const clientHeaders = await getClientHeaders();
        if (clientHeaders && Object.keys(clientHeaders).length > 0 && config.headers) {
          // 将客户端请求头合并到当前请求的headers中
          Object.assign(config.headers, clientHeaders);
          console.log('已添加客户端请求头到请求中:', clientHeaders);
        }
      } catch (error) {
        console.warn('添加客户端请求头失败:', error);
      }

      // 测试
      // if (__ENV__ === 'dev') {
      //   const clientHeaders = {
      //     'X-device-id': 'test-device 31bc044f-56cf-423c-a7cf-a697c87255d2'
      //   };
      //   Object.assign(config.headers, clientHeaders);
      // }
      
      // 可以添加请求loading
      // showLoading();

      // 自定义环境（测试环境下生效）
      console.log('import.meta.env.VITE_APP_ENV', import.meta.env.VITE_APP_ENV);
      if (import.meta.env.VITE_APP_ENV === 'dev') {
        const envAddressStr = localStorage.getItem('jhs_env_address')
        console.log('envAddressStr', envAddressStr);
        if (envAddressStr) {
          try {
            const envAddress = JSON.parse(envAddressStr)
            console.log('envAddress', envAddress);
            if (envAddress) {
              config.baseURL = envAddress
            }
          } catch (e) {
            console.log('解析自定义环境失败', e);
          }
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // 隐藏loading
      // hideLoading();
      const res = response.data;
      // 如果响应数据中没有 code 字段，说明是直接返回的数据
      if (!('code' in res)) {
        return response;
      }
      // 有 code 字段的情况，根据业务状态码处理
      if (res.code !== 0 && res.code !== 200) {
        // 统一处理错误，直接输出服务端返回的错误信息

        // console.error(res.message || res.msg || '请求出错');
        // return Promise.reject(new Error(res.message || res.msg || '请求出错'));
        // 创建错误对象，并附加完整的响应数据
        console.log('原始返回数据', res);
        const error: any = new Error(res.message || res.msg || '请求出错');
        error.code = res.code;
        error.errorType = res.error;
        error.response = response;
        return Promise.reject(error);
      }
      // 修改这里，返回完整的 response
      response.data = res.data;
      return response;
    },
    (error) => {
      // 隐藏loading
      // hideLoading();
      // 处理HTTP状态码错误
      if (error.response) {
        // 直接输出服务端返回的错误信息
        const res = error.response.data || {};
        const message = res.message || res.msg || '请求出错';
        // 401处理逻辑保留
        if (error.response.status === 401) {
          try {
            // const userStore = useUserStore();
            // userStore.logout();
          } catch (e) {
            // 忽略用户store相关错误
            console.warn('用户store处理失败:', e);
          }
          if (typeof window !== 'undefined' && window.jhsapp && typeof window.jhsapp.router === 'function') {
            window.jhsapp.router('https://www.jihuanshe.com/app/login');
          }
        }
        console.error(message);
        // 将完整的错误信息附加到 error 对象上
        error.code = res.code;
        error.errorType = res.error;
      } else {
        if (error.message && error.message.includes('timeout')) {
          console.error('请求超时！');
        } else {
          console.error('网络连接异常！');
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// 创建axios实例
const axiosInstance = createAxiosInstance();

// 封装请求方法
const http = {
  request<T = any>(
    method: HttpMethod,
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<BaseResponse<T>> {
    const config: AxiosRequestConfig = {
      url,
      method,
    };

    // 处理请求参数
    if (method.toUpperCase() === 'GET' || method.toUpperCase() === 'DELETE') {
      config.params = data;
    } else {
      config.data = data;
    }

    // 合并自定义配置
    if (options) {
      if (options.timeout) {
        config.timeout = options.timeout;
      }

      if (options.headers) {
        config.headers = { ...config.headers, ...options.headers };
      }

      if (options.contentType) {
        config.headers = {
          ...config.headers,
          'Content-Type': options.contentType,
        };
      }
    }

    return axiosInstance.request(config) as Promise<BaseResponse<T>>;
  },

  get<T = any>(url: string, params?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>('GET', url, params, options);
  },

  post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>('POST', url, data, options);
  },

  put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>('PUT', url, data, options);
  },

  delete<T = any>(url: string, params?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>('DELETE', url, params, options);
  },

  patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>('PATCH', url, data, options);
  },

  // 上传文件
  uploadFile<T = any>(url: string, formData: FormData, options?: RequestOptions): Promise<BaseResponse<T>> {
    const uploadOptions: RequestOptions = {
      contentType: 'multipart/form-data',
      ...options,
    };
    return this.post(url, formData, uploadOptions);
  },
};

// 为axios实例添加API埋点统计拦截器
export const addAPITracking = () => {
  // 动态导入以避免循环依赖
  import('../composables/useAPITracking').then(({ useAPITracking }) => {
    const { createAxiosInterceptor } = useAPITracking();
    createAxiosInterceptor(axiosInstance);
    // console.log('✅ API埋点统计拦截器已添加到axios实例');
  }).catch(error => {
    console.warn('⚠️ 无法添加API埋点统计拦截器:', error);
  });
};

export default http; 